import { useState } from "react";
import { format, isToday, isThisWeek } from "date-fns";
import {
  Plus,
  MessageSquare,
  Phone,
  Mail,
  Users,
  ArrowRightLeft,
  StickyNote,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { PipelineActivity, useAddActivity } from "@/hooks/usePipeline";
import { VoiceNoteInput } from "@/components/pipeline/VoiceNoteInput";
import { toast } from "sonner";

interface EngagementTimelineTabProps {
  opportunityId: string;
  activities: PipelineActivity[];
}

const ACTIVITY_ICONS: Record<string, typeof MessageSquare> = {
  note: StickyNote,
  call: Phone,
  email: Mail,
  meeting: Users,
  stage_change: ArrowRightLeft,
};

const ACTIVITY_COLORS: Record<string, string> = {
  note: "bg-gray-100 text-gray-600",
  call: "bg-blue-100 text-blue-600",
  email: "bg-purple-100 text-purple-600",
  meeting: "bg-green-100 text-green-600",
  stage_change: "bg-amber-100 text-amber-600",
};

function groupActivities(activities: PipelineActivity[]) {
  const groups: { label: string; items: PipelineActivity[] }[] = [];
  const today: PipelineActivity[] = [];
  const thisWeek: PipelineActivity[] = [];
  const earlier: PipelineActivity[] = [];

  for (const a of activities) {
    const date = new Date(a.activity_date);
    if (isToday(date)) today.push(a);
    else if (isThisWeek(date)) thisWeek.push(a);
    else earlier.push(a);
  }

  if (today.length > 0) groups.push({ label: "Today", items: today });
  if (thisWeek.length > 0) groups.push({ label: "This Week", items: thisWeek });
  if (earlier.length > 0) groups.push({ label: "Earlier", items: earlier });

  return groups;
}

export function EngagementTimelineTab({
  opportunityId,
  activities,
}: EngagementTimelineTabProps) {
  const addActivity = useAddActivity();
  const [activityType, setActivityType] = useState("note");
  const [activityTitle, setActivityTitle] = useState("");
  const [activityDescription, setActivityDescription] = useState("");
  const [showDescription, setShowDescription] = useState(false);

  const handleAdd = () => {
    if (!activityTitle.trim()) return;
    addActivity.mutate(
      {
        opportunity_id: opportunityId,
        activity_type: activityType,
        title: activityTitle.trim(),
        description: activityDescription.trim() || null,
        activity_date: new Date().toISOString(),
      },
      {
        onSuccess: () => {
          toast.success("Activity logged");
          setActivityTitle("");
          setActivityDescription("");
          setShowDescription(false);
        },
        onError: () => toast.error("Failed to log activity"),
      }
    );
  };

  const groups = groupActivities(activities);

  return (
    <div className="space-y-5">
      {/* Add Activity Form */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Log Activity
          </h4>
          <div className="flex gap-2">
            <Select value={activityType} onValueChange={setActivityType}>
              <SelectTrigger className="w-[120px] h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="note">Note</SelectItem>
                <SelectItem value="call">Call</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="meeting">Meeting</SelectItem>
              </SelectContent>
            </Select>
            <Input
              placeholder="What happened?"
              value={activityTitle}
              onChange={(e) => setActivityTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !showDescription && handleAdd()}
              className="flex-1 h-9"
            />
            <VoiceNoteInput
              contextId={`activity-${opportunityId}`}
              onTranscript={(text) => {
                setActivityDescription((prev) =>
                  prev ? `${prev}\n\n${text}` : text
                );
                setShowDescription(true);
              }}
            />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowDescription(!showDescription)}
              className="h-9 text-xs"
            >
              {showDescription ? "Less" : "+ Detail"}
            </Button>
            <Button
              size="sm"
              onClick={handleAdd}
              disabled={!activityTitle.trim() || addActivity.isPending}
              className="h-9"
            >
              <Plus className="h-4 w-4 mr-1" />
              Log
            </Button>
          </div>
          {showDescription && (
            <Textarea
              placeholder="Additional details..."
              value={activityDescription}
              onChange={(e) => setActivityDescription(e.target.value)}
              rows={2}
              className="text-sm"
            />
          )}
        </CardContent>
      </Card>

      {/* Timeline */}
      {activities.length === 0 ? (
        <div className="text-center py-12">
          <MessageSquare className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No activities recorded yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Log your first call, email, or meeting above
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {groups.map((group) => (
            <div key={group.label}>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                {group.label}
              </h4>
              <div className="space-y-2">
                {group.items.map((activity) => {
                  const Icon =
                    ACTIVITY_ICONS[activity.activity_type] || MessageSquare;
                  const colorClass =
                    ACTIVITY_COLORS[activity.activity_type] || "bg-gray-100 text-gray-600";

                  return (
                    <div
                      key={activity.id}
                      className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-muted/30 transition-colors"
                    >
                      <div className={`p-1.5 rounded-md shrink-0 ${colorClass}`}>
                        <Icon className="h-3.5 w-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{activity.title}</p>
                        {activity.description && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {activity.description}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-[10px] px-1.5">
                            {activity.activity_type}
                          </Badge>
                          <span className="text-[10px] text-muted-foreground">
                            {format(new Date(activity.activity_date), "MMM d, yyyy 'at' h:mm a")}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
